import { Locales } from '@griply/types';

import { getDayMap, getRruleKeywords } from './locale-vocabulary';

export enum Gender {
  Masculine = 'masculine',
  Feminine = 'feminine',
}

export enum OrdinalPosition {
  First = 'first',
  Second = 'second',
  Third = 'third',
  Fourth = 'fourth',
  Fifth = 'fifth',
  Last = 'last',
}

// Polish weekdays follow "co" / "w", which take the accusative — feminine days differ from the nominative.
const DAY_RENDER_OVERRIDES: Partial<Record<Locales, Record<string, string>>> = {
  [Locales.PlPL]: {
    MO: 'poniedziałek',
    TU: 'wtorek',
    WE: 'środę',
    TH: 'czwartek',
    FR: 'piątek',
    SA: 'sobotę',
    SU: 'niedzielę',
  },
};

// Both Portuguese variants share one date grammar; only UI wording differs.
const portugueseDayGenders: Record<string, Gender> = {
  MO: Gender.Feminine,
  TU: Gender.Feminine,
  WE: Gender.Feminine,
  TH: Gender.Feminine,
  FR: Gender.Feminine,
  SA: Gender.Masculine,
  SU: Gender.Masculine,
};

const DAY_GENDERS: Partial<Record<Locales, Record<string, Gender>>> = {
  // Italian: only "domenica" is feminine.
  [Locales.ItIT]: {
    MO: Gender.Masculine,
    TU: Gender.Masculine,
    WE: Gender.Masculine,
    TH: Gender.Masculine,
    FR: Gender.Masculine,
    SA: Gender.Masculine,
    SU: Gender.Feminine,
  },
  // Portuguese: the "-feira" weekdays are feminine; sábado and domingo are not.
  [Locales.PtPT]: portugueseDayGenders,
  [Locales.PtBR]: portugueseDayGenders,
  [Locales.PlPL]: {
    MO: Gender.Masculine,
    TU: Gender.Masculine,
    WE: Gender.Feminine,
    TH: Gender.Masculine,
    FR: Gender.Masculine,
    SA: Gender.Feminine,
    SU: Gender.Feminine,
  },
};

// Both Portuguese variants share one date grammar; only UI wording differs.
const portugueseOrdinals: Record<OrdinalPosition, Record<Gender, string>> = {
  [OrdinalPosition.First]: {
    [Gender.Masculine]: 'primeiro',
    [Gender.Feminine]: 'primeira',
  },
  [OrdinalPosition.Second]: {
    [Gender.Masculine]: 'segundo',
    [Gender.Feminine]: 'segunda',
  },
  [OrdinalPosition.Third]: {
    [Gender.Masculine]: 'terceiro',
    [Gender.Feminine]: 'terceira',
  },
  [OrdinalPosition.Fourth]: {
    [Gender.Masculine]: 'quarto',
    [Gender.Feminine]: 'quarta',
  },
  [OrdinalPosition.Fifth]: {
    [Gender.Masculine]: 'quinto',
    [Gender.Feminine]: 'quinta',
  },
  [OrdinalPosition.Last]: {
    [Gender.Masculine]: 'último',
    [Gender.Feminine]: 'última',
  },
};

const ORDINAL_OVERRIDES: Partial<
  Record<Locales, Record<OrdinalPosition, Record<Gender, string>>>
> = {
  [Locales.PlPL]: {
    [OrdinalPosition.First]: {
      [Gender.Masculine]: 'pierwszy',
      [Gender.Feminine]: 'pierwszą',
    },
    [OrdinalPosition.Second]: {
      [Gender.Masculine]: 'drugi',
      [Gender.Feminine]: 'drugą',
    },
    [OrdinalPosition.Third]: {
      [Gender.Masculine]: 'trzeci',
      [Gender.Feminine]: 'trzecią',
    },
    [OrdinalPosition.Fourth]: {
      [Gender.Masculine]: 'czwarty',
      [Gender.Feminine]: 'czwartą',
    },
    [OrdinalPosition.Fifth]: {
      [Gender.Masculine]: 'piąty',
      [Gender.Feminine]: 'piątą',
    },
    [OrdinalPosition.Last]: {
      [Gender.Masculine]: 'ostatni',
      [Gender.Feminine]: 'ostatnią',
    },
  },
  [Locales.ItIT]: {
    [OrdinalPosition.First]: {
      [Gender.Masculine]: 'primo',
      [Gender.Feminine]: 'prima',
    },
    [OrdinalPosition.Second]: {
      [Gender.Masculine]: 'secondo',
      [Gender.Feminine]: 'seconda',
    },
    [OrdinalPosition.Third]: {
      [Gender.Masculine]: 'terzo',
      [Gender.Feminine]: 'terza',
    },
    [OrdinalPosition.Fourth]: {
      [Gender.Masculine]: 'quarto',
      [Gender.Feminine]: 'quarta',
    },
    [OrdinalPosition.Fifth]: {
      [Gender.Masculine]: 'quinto',
      [Gender.Feminine]: 'quinta',
    },
    [OrdinalPosition.Last]: {
      [Gender.Masculine]: 'ultimo',
      [Gender.Feminine]: 'ultima',
    },
  },
  [Locales.PtPT]: portugueseOrdinals,
  [Locales.PtBR]: portugueseOrdinals,
};

const longestNameForCode = (language: Locales) => {
  const dayMap = getDayMap(language);
  return Object.entries(dayMap).reduce<Record<string, string>>(
    (acc, [name, code]) => {
      if (!acc[code] || name.length > acc[code].length) {
        acc[code] = name;
      }
      return acc;
    },
    {},
  );
};

const renderFormCache = new Map<Locales, Record<string, string>>();

export const getDayRenderForms = (language: Locales) => {
  const cached = renderFormCache.get(language);
  if (cached) {
    return cached;
  }

  const forms = DAY_RENDER_OVERRIDES[language] ?? longestNameForCode(language);
  renderFormCache.set(language, forms);
  return forms;
};

// Italian and Portuguese contract the preposition with the following noun's gender.
const ON_OVERRIDES: Partial<Record<Locales, Record<Gender, string>>> = {
  [Locales.ItIT]: { [Gender.Masculine]: 'il', [Gender.Feminine]: 'la' },
  [Locales.PtPT]: { [Gender.Masculine]: 'no', [Gender.Feminine]: 'na' },
  [Locales.PtBR]: { [Gender.Masculine]: 'no', [Gender.Feminine]: 'na' },
};

export const getOnForm = (gender: Gender, language: Locales) => {
  const override = ON_OVERRIDES[language]?.[gender];
  if (override) {
    return override;
  }

  const keywords = getRruleKeywords(language);
  return keywords.on_simple;
};

export const getDayGender = (code: string, language: Locales) =>
  DAY_GENDERS[language]?.[code] ?? Gender.Masculine;

type OrdinalSuffixRule = {
  default: string;
  byNumber?: Record<number, string>;
  // Read off the last digit, except in the teens ("11th", not "11st").
  byLastDigit?: Record<number, string>;
};

const ORDINAL_SUFFIXES: Record<Locales, OrdinalSuffixRule> = {
  [Locales.EnUS]: { default: 'th', byLastDigit: { 1: 'st', 2: 'nd', 3: 'rd' } },
  [Locales.NlNL]: { default: 'e' },
  [Locales.EsES]: { default: 'º' },
  [Locales.DeDE]: { default: '.' },
  [Locales.PlPL]: { default: '.' },
  [Locales.DaDK]: { default: '.' },
  [Locales.SvSE]: { default: ':e', byLastDigit: { 1: ':a', 2: ':a' } },
  [Locales.NbNO]: { default: '.' },
  [Locales.ItIT]: { default: 'º' },
  [Locales.FrFR]: { default: 'e', byNumber: { 1: 'er' } },
  [Locales.PtPT]: { default: 'º' },
  [Locales.PtBR]: { default: 'º' },
};

export const getOrdinalSuffix = (value: string, language: Locales) => {
  const rule = ORDINAL_SUFFIXES[language] ?? ORDINAL_SUFFIXES[Locales.EnUS];
  const day = Math.abs(parseInt(value, 10));

  const exact = rule.byNumber?.[day];
  if (exact) {
    return exact;
  }
  if (day % 100 >= 11 && day % 100 <= 13) {
    return rule.default;
  }

  return rule.byLastDigit?.[day % 10] ?? rule.default;
};

export const getOrdinalForm = (
  position: OrdinalPosition,
  gender: Gender,
  language: Locales,
) => {
  const override = ORDINAL_OVERRIDES[language]?.[position][gender];
  if (override) {
    return override;
  }

  const keywords = getRruleKeywords(language);
  return keywords[`${position}_simple`];
};
